import React, { useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { API_CONFIG } from '../config/config';
import logo from '../assets/logo_blau.png';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const validationSchema = yup.object({
  new_password: yup
    .string()
    .min(12, 'Das Passwort sollte mindestens 12 Zeichen lang sein')
    .max(128, 'Das Passwort sollte nicht länger als 128 Zeichen sein')
    .matches(/^[\s\S]*$/, 'Das Passwort kann beliebige Zeichen enthalten')
    .required('Neues Passwort ist erforderlich'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleClickShowPassword = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      new_password: '',
      confirm_password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError('');
      setLoading(true);

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/password-reset/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            ...values,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 429) {
            // Handle rate limit error
            const retryAfter = response.headers.get('Retry-After');
            const waitTime = retryAfter ? parseInt(retryAfter) : 60;
            throw new Error(`Too many requests. Please wait ${waitTime} seconds before trying again.`);
          }
          throw new Error(data.detail || 'Failed to reset password');
        }

        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
  });

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <Fade in={true} timeout={500}>
            <Box>
              <CheckCircleOutlineIcon 
                sx={{ 
                  fontSize: 60, 
                  color: 'success.main',
                  mb: 2
                }} 
              />
              <Typography variant="h5" gutterBottom>
                Passwort erfolgreich zurückgesetzt
              </Typography>
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  '& .MuiAlert-message': {
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              >
                Dein Passwort wurde erfolgreich zurückgesetzt. Du wirst in Kürze zur Anmeldeseite weitergeleitet.
              </Alert>
            </Box>
          </Fade>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <img
            src={logo}
            alt="CyberCustos Logo"
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </Box>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          Passwort zurücksetzen
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={formik.handleSubmit} noValidate>
          <TextField
            fullWidth
            id="new_password"
            name="new_password"
            label="New Password"
            type={showPassword.new ? 'text' : 'password'}
            value={formik.values.new_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.new_password && Boolean(formik.errors.new_password)}
            helperText={formik.touched.new_password && formik.errors.new_password}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('new')}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordStrengthMeter password={formik.values.new_password} />
          <TextField
            fullWidth
            id="confirm_password"
            name="confirm_password"
            label="Confirm New Password"
            type={showPassword.confirm ? 'text' : 'password'}
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
            helperText={formik.touched.confirm_password && formik.errors.confirm_password}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('confirm')}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              mt: 3,
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333333',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Zurück zur Anmeldeseite
            </Link>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ResetPassword; 